/**
 * Schools API Service
 * Handles school search export interface OpenRostersResponse {
  success: boolean;
  matched_schools: MatchedSchool[]; // Changed from roster_openings to matched_schools
  athlete_graduation_year: number;
  query?: string; // Only included if search query was provided
  pagination: {
    current_page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  total: number; // Number of results in current response
}rations
 */

import { BACKEND_CONFIG } from '../config/backendConfig';

// Types for school data
export interface School {
  school_id: number;
  name: string;
  ncaa_name?: string;
  academic_name?: string;
  transfer_portal_name?: string;
  division?: string;
  conference?: string;
  head_coach?: string;
  max_season_year?: string;
  season_url?: string;
  school_sports_url?: string;
  college_url?: string;
  logo_url?: string;
  current_season?: boolean;
  last_updated?: string;
  academic_school_id?: string;
  transfer_portal_school_id?: string;
  
  // Roster opening data (when fetched from open rosters endpoint)
  estimated_graduation_year?: number;
  openings?: number;
  roster_status?: 'Open Roster' | 'Limited Spots' | 'No Openings';
}

export interface SchoolSearchResponse {
  success: boolean;
  matched_schools: MatchedSchool[]; // Changed from results: School[] - now same structure as MatchedForYouResponse
  total: number;
  query: string;
  pagination: {
    current_page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  filters_applied?: {
    open_rosters_only?: boolean;
    athlete_graduation_year?: number;
  };
}

export interface OpenRostersResponse {
  success: boolean;
  matched_schools: MatchedSchool[]; // Changed from roster_openings - now same structure as MatchedForYouResponse
  athlete_graduation_year: number;
  query?: string; // Only included if search query was provided
  pagination: {
    current_page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  total: number; // Number of results in current response
}

export interface MatchedSchool extends School {
  // Roster Opening Data
  estimated_graduation_year: number;
  openings: number;
  roster_status: "Open Roster" | "Limited Spots" | "No Openings";
  
  // Matching Scores (50-100 scale)
  match_score_pct: number;      // Raw calculated score
  match_score_norm: number;     // Normalized score (main ranking)
  pos_score: number;            // Position fit score
  geo_score: number;            // Geographic recruiting fit
  state_score: number;          // In-state bonus (100 if same state)
  academic_score: number;       // Academic fit score
  athlete_score: number;        // Athletic performance fit
  athlete_position: string;
  
  // Academic Data (only for matched schools)
  acceptance_rate_pct: number | null;
  cost_per_year_k: number | null;
  undergraduate_student_count: number | null;
  
  // School Location Data
  school_city: string | null;
  school_state: string | null;

  // Majors count
  majors_count?: number;
}

export interface MatchedForYouResponse {
  success: boolean;
  matched_schools: MatchedSchool[];
  query?: string; // Search query used (null if no search)
  pagination: {
    current_page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  total: number; // Number of results in current response
}

// Comprehensive school data types
export interface CoachingStaffMember {
  production_id: number;
  coach_name: string;
  position: string;
  title?: string;
  email?: string;
  phone?: string;
  twitter_handle?: string;
  instagram_handle?: string;
  bio_summary?: string;
  source_type?: string;
  approved_by?: string;
  approved_at?: string;
  created_at?: string;
  updated_at?: string;
  // Phase 2.5: backend emits these flags for both paid and free responses
  // so the frontend can render gated affordances without knowing whether
  // the value above is null due to redaction or genuinely missing data.
  has_email?: boolean;
  has_phone?: boolean;
  has_twitter_handle?: boolean;
  has_instagram_handle?: boolean;
}

export interface AcademicStats {
  unitid?: string;
  school_name?: string;
  school_url?: string;
  city?: string;
  state?: string;
  zip?: string;
  federal_school_code?: string;
  school_type?: string;
  latitude?: number;
  longitude?: number;
  women_only?: boolean;
  men_only?: boolean;
  admission_rate?: number;
  avg_sat_score?: number;
  avg_math_sat_score?: number;
  avg_reading_sat_score?: number;
  avg_act_score?: number;
  avg_english_act_score?: number;
  avg_math_act_score?: number;
  avg_cost_of_attendance?: number;
  avg_room_board_off_campus_cost?: number;
  avg_room_board_on_campus_cost?: number;
  avg_tuition_out_of_state?: number;
  avg_tuition_in_state?: number;
  undergraduate_student_count?: number;
  graduate_student_count?: number;
  undegraduate_women_percent?: number;
  undegraduate_men_percent?: number;
  percent_undergraduates_pell_grant?: number;
  percent_undergraduates_with_federal_loan?: number;
  completion_rate_undergraduate?: number;
  transfer_rate_undergraduate?: number;
  snapshot_date_time?: string;
}

export interface AcademicInformation {
  academic_year?: string;
  unitid?: string;
  opeid?: string;
  opeid_6?: string;
  institution_name?: string;
  city?: string;
  state?: string;
  zip?: string;
  accrediting_agency?: string;
  institution_url?: string;
  net_price_calculator_url?: string;
  field_name?: string;
  value?: string;
  dev_category?: string;
  api_data_type?: string;
  data_element_name?: string;
  start_year?: number;
  end_year?: number;
  refresh_date_time?: string;
}

export interface TopRecruitingState {
  school_id: number;
  hometown_state: string;
  total_player_counts: number;
  player_counts: number;
  percent_of_total_players: number;
  snapshot_date_time: string;
}

// Matching data from matched-for-you endpoint
export interface MatchingData {
  school_id: number;
  school_city: string | null;
  school_state: string | null;
  undergraduate_student_count: number | null;
  acceptance_rate_pct: number | null;
  cost_per_year_k: number | null;
  estimated_graduation_year: number;
  openings: number;
  roster_status: string;
  match_score_pct: number;
  match_score_norm: number;
  pos_score: number;
  geo_score: number;
  state_score: number;
  academic_score: number;
  athlete_score: number;
  athlete_position: string;
}

// Position openings data
export interface PositionOpening {
  school_id: number;
  division: string;
  season: number;
  estimated_graduation_year: number;
  position_name: string;
  count: number;
}

export interface RosterData {
  roster_id: string;
  ncaa_team_id: string;
  player_id: string;
  season: string;
  ending_season_year: number;
  player_url?: string;
  jersey_number?: string;
  player_name: string;
  class_year: string;
  class_year_name: string;
  estimated_graduation_year: number;
  position: string;
  position_name: string;
  games_played: number;
  games_started: number;
  height?: string;
  bats?: string;
  throws?: string;
  hometown: string;
  hometown_city: string;
  hometown_state: string;
  high_school?: string;
  // Batting Statistics
  at_bats: number;
  runs: number;
  hits: number;
  batting_average: number;
  on_base_percentage: number;
  fielding_percentage: number;
  // Pitching Statistics
  innings_pitched: number;
  batters_faced: number;
  runs_allowed: number;
  earned_runs: number;
  walks_allowed: number;
  earned_run_average: number;
  created_at: string;
  updated_at: string;
}

export interface SchoolStats {
  school_id: number;
  ncaa_team_id: string;
  season: string;
  current_season: boolean;
  wins: number;
  losses: number;
  win_pct: number;
  player_count: number;
}

export interface TransferPortalStats {
  year: number;
  transfers_in: number;
  transfers_out: number;
  net: number;
}

export interface ComprehensiveSchool extends Omit<School, 'max_season_year'> {
  /** The athlete's real class. Openings may use the school's newest class instead. */
  athlete_graduation_year?: number | null;
  // Basic extended info
  ncaa_name?: string;
  academic_name?: string;
  transfer_portal_name?: string;
  logo_url?: string;
  max_season_year?: number; // Override as number in comprehensive data
  season_url?: string;
  school_sports_url?: string;
  college_url?: string;
  current_season?: boolean;
  last_updated?: string;
  academic_school_id?: string;
  transfer_portal_school_id?: string;
  
  // Comprehensive data arrays/objects
  coaching_staff?: CoachingStaffMember[];
  academic_stats?: AcademicStats;
  academic_information?: AcademicInformation[];
  top_recruiting_states?: TopRecruitingState[];
  roster_data?: RosterData[];
  school_stats?: SchoolStats;
  transfer_portal_stats?: TransferPortalStats | null;
  degrees?: string[];
  
  // Enhanced athlete-specific data (when athlete_id is provided)
  matching_data?: MatchingData | null;
  position_openings?: PositionOpening[];
}

export interface ComprehensiveSchoolResponse {
  success: boolean;
  school: ComprehensiveSchool;
}

// API utility function
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string
): Promise<T> {
  const url = `${BACKEND_CONFIG.baseURL}${endpoint}`;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });
    
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || error.error || `HTTP ${response.status}: ${error.message || 'Unknown error'}`);
  }

  const responseData = await response.json();
  return responseData;
}

// Schools API
export const schoolsAPI = {
  // Search schools using POST method with advanced search
  async searchSchools(
    token: string,
    query: string,
    filters?: {
      division?: string;
      conference?: string;
      limit?: number;
      offset?: number;
      open_rosters_only?: boolean;
      athlete_id?: string;
    }
  ): Promise<SchoolSearchResponse> {
    const searchData: any = { query };
    
    if (filters) {
      if (filters.limit) searchData.limit = filters.limit;
      if (filters.offset) searchData.offset = filters.offset;
      if (filters.open_rosters_only) searchData.open_rosters_only = filters.open_rosters_only;
      if (filters.athlete_id) searchData.athlete_id = filters.athlete_id;
      
      if (filters.division || filters.conference) {
        searchData.filters = {};
        if (filters.division) searchData.filters.division = filters.division;
        if (filters.conference) searchData.filters.conference = filters.conference;
      }
    }

    return apiCall('/schools/search', {
      method: 'POST',
      body: JSON.stringify(searchData),
    }, token);
  },

  // Search schools using GET method (simpler)
  async searchSchoolsGet(
    token: string,
    query: string,
    offset: number = 0,
    limit: number = 20,
    options?: {
      open_rosters_only?: boolean;
      athlete_id?: string;
      signal?: AbortSignal;
    }
  ): Promise<SchoolSearchResponse> {
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('offset', offset.toString());
    params.append('limit', limit.toString());
    
    if (options?.open_rosters_only) {
      params.append('open_rosters_only', 'true');
    }
    if (options?.athlete_id) {
      params.append('athlete_id', options.athlete_id);
    }
    
    return apiCall(`/schools/search?${params.toString()}`, { 
      method: 'GET',
      signal: options?.signal // Pass the abort signal
    }, token);
  },

  // Get detailed school information
  async getSchool(
    token: string,
    schoolId: number
  ): Promise<{
    success: boolean;
    school: School;
  }> {
    return apiCall(`/schools/${schoolId}`, { method: 'GET' }, token);
  },

  // List schools with filtering and pagination
  async listSchools(
    token: string,
    options?: {
      division?: string;
      conference?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    success: boolean;
    results: School[];
    total: number;
    pagination: {
      limit: number;
      offset: number;
      has_more: boolean;
    };
  }> {
    const params = new URLSearchParams();
    if (options?.division) params.append('division', options.division);
    if (options?.conference) params.append('conference', options.conference);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    
    const queryString = params.toString();
    const endpoint = `/schools${queryString ? `?${queryString}` : ''}`;
    
    return apiCall(endpoint, { method: 'GET' }, token);
  },

  // Get roster opening stats for an athlete
  async getRosterStats(
    token: string,
    athleteId: string
  ): Promise<{
    success: boolean;
    data: {
      athlete_graduation_year: number;
      total_schools_with_openings: number;
      total_available_spots: number;
      breakdown_by_division: {
        'Division I': number;
        'Division II': number;
        'Division III': number;
      };
      last_updated: string;
    };
  }> {
    return apiCall(`/athletes/${athleteId}/roster-stats`, { method: 'GET' }, token);
  },

  // Get schools with open rosters (POST method)
  async getOpenRosters(
    token: string,
    athleteId: string,
    options?: {
      limit?: number;
      offset?: number;
      query?: string;
      signal?: AbortSignal;
    }
  ): Promise<OpenRostersResponse> {
    const requestData = {
      athlete_id: athleteId,
      limit: options?.limit || 50,
      offset: options?.offset || 0,
      ...(options?.query && { query: options.query }), // Add query parameter if provided
    };

    return apiCall('/schools/open-rosters', {
      method: 'POST',
      body: JSON.stringify(requestData),
      signal: options?.signal, // Pass the abort signal
    }, token);
  },

  // Get schools with open rosters (GET method alternative)
  async getOpenRostersGet(
    token: string,
    athleteId: string,
    options?: {
      limit?: number;
      offset?: number;
      query?: string;
    }
  ): Promise<OpenRostersResponse> {
    const params = new URLSearchParams();
    // Ensure limit and offset are properly converted to strings
    if (options?.limit !== undefined) params.append('limit', String(options.limit));
    if (options?.offset !== undefined) params.append('offset', String(options.offset));
    if (options?.query) params.append('q', options.query); // Use 'q' parameter as per backend spec
    
    const queryString = params.toString();
    const endpoint = `/schools/open-rosters/${athleteId}${queryString ? `?${queryString}` : ''}`;
    
    return apiCall(endpoint, { method: 'GET' }, token);
  },

  // Get matched schools for athlete (POST method)
  async getMatchedForYou(
    token: string,
    athleteId: string,
    options?: {
      limit?: number;
      offset?: number;
      query?: string;
      signal?: AbortSignal;
    }
  ): Promise<MatchedForYouResponse> {
    const requestData = {
      athlete_id: athleteId,
      limit: options?.limit || 50,
      offset: options?.offset || 0,
      include_academic_data: true, // Request academic fields
      ...(options?.query && { query: options.query }),
    };

    return apiCall('/schools/matched-for-you', {
      method: 'POST',
      body: JSON.stringify(requestData),
      signal: options?.signal, // Pass the abort signal
    }, token);
  },

  // Get single comprehensive school data
  async getComprehensiveSchool(
    token: string,
    schoolId: number,
    athleteId?: string
  ): Promise<{
    success: boolean;
    school: ComprehensiveSchool;
  }> {
    const requestData = {
      school_id: schoolId,
      ...(athleteId && { athlete_id: athleteId }),
    };

    return apiCall('/schools/comprehensive', {
      method: 'POST',
      body: JSON.stringify(requestData),
    }, token);
  },

  // Filter schools using custom filters
  async filterSchools(
    token: string,
    athleteId: string,
    filters: {
      division?: string[];
      conference?: string[];
      state?: string[];
      acceptanceRateMin?: number | null;
      acceptanceRateMax?: number | null;
      costMin?: number | null;
      costMax?: number | null;
      studentCountMin?: number | null;
      studentCountMax?: number | null;
      rosterStatus?: string[];
      positions?: string[];
      recruitingStates?: string[];
      majors?: string[];
      limit?: number;
      offset?: number;
      query?: string;
      signal?: AbortSignal;
    }
  ): Promise<MatchedForYouResponse> {
    const requestData = {
      athlete_id: athleteId,
      limit: filters.limit || 20,
      offset: filters.offset || 0,
      ...(filters.query && { query: filters.query }),
      ...(filters.division && { division: filters.division }),
      ...(filters.conference && { conference: filters.conference }),
      ...(filters.state && { state: filters.state }),
      ...(filters.acceptanceRateMin !== null && filters.acceptanceRateMin !== undefined && { acceptanceRateMin: filters.acceptanceRateMin }),
      ...(filters.acceptanceRateMax !== null && filters.acceptanceRateMax !== undefined && { acceptanceRateMax: filters.acceptanceRateMax }),
      ...(filters.costMin !== null && filters.costMin !== undefined && { costMin: filters.costMin }),
      ...(filters.costMax !== null && filters.costMax !== undefined && { costMax: filters.costMax }),
      ...(filters.studentCountMin !== null && filters.studentCountMin !== undefined && { studentCountMin: filters.studentCountMin }),
      ...(filters.studentCountMax !== null && filters.studentCountMax !== undefined && { studentCountMax: filters.studentCountMax }),
      ...(filters.rosterStatus && { rosterStatus: filters.rosterStatus }),
      ...(filters.positions && { positions: filters.positions }),
      ...(filters.recruitingStates && { recruitingStates: filters.recruitingStates }),
      ...(filters.majors && { majors: filters.majors }),
    };

    const response = await apiCall('/schools/filter', {
      method: 'POST',
      body: JSON.stringify(requestData),
      signal: filters.signal, // Pass the abort signal
    }, token);

    return response as MatchedForYouResponse;
  },

  // Get filter options for the filter modal
  async getFilterOptions(
    token: string
  ): Promise<{
    success: boolean;
    filter_options: {
      divisions?: string[];
      conferences?: string[];
      states?: string[];
      rosterStatuses?: string[];
      positions?: string[];
      recruitingStates?: string[];
      majors?: string[];
      acceptanceRateRange?: { min: number; max: number };
      costRange?: { min: number; max: number };
      studentCountRange?: { min: number; max: number };
    };
    meta: any;
  }> {
    return apiCall('/schools/filter-options', { method: 'GET' }, token);
  },
};
